// A thin-plate spline: the smoothest map that takes each of a few points to a chosen place.
// The X-ray uses it to move the skeleton's dots onto the face.

export interface Point {
  x: number;
  y: number;
}

// The spline's kernel, r² log r, written for the squared distance.
function kernel(squared: number): number {
  return squared === 0 ? 0 : 0.5 * squared * Math.log(squared);
}

// Solves a * x = b for a small dense system (Gaussian elimination, partial pivoting).
function solve(a: number[][], b: number[]): number[] {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i]!]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(m[row]![col]!) > Math.abs(m[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(m[pivot]![col]!) < 1e-12) throw new Error("The landmarks are degenerate");
    [m[col], m[pivot]] = [m[pivot]!, m[col]!];
    for (let row = col + 1; row < n; row++) {
      const factor = m[row]![col]! / m[col]![col]!;
      for (let k = col; k <= n; k++) m[row]![k]! -= factor * m[col]![k]!;
    }
  }
  const x = Array.from({ length: n }, () => 0);
  for (let row = n - 1; row >= 0; row--) {
    let sum = m[row]![n]!;
    for (let k = row + 1; k < n; k++) sum -= m[row]![k]! * x[k]!;
    x[row] = sum / m[row]![row]!;
  }
  return x;
}

/** The thin-plate spline that takes each point of `from` to the point of `to` at the same
    index, and bends least between them. It reproduces an affine map exactly. */
export function thinPlateSpline(
  from: readonly Point[],
  to: readonly Point[],
): (point: Point) => Point {
  if (from.length !== to.length || from.length < 3) {
    throw new Error("A thin-plate spline needs three or more pairs");
  }
  const n = from.length;
  // The solve works in thousands of units, which keeps the kernel's terms near the
  // affine terms' size.
  const unit = 1000;
  const p = from.map(({ x, y }) => ({ x: x / unit, y: y / unit }));
  const size = n + 3;
  const a = Array.from({ length: size }, () => Array.from({ length: size }, () => 0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const dx = p[i]!.x - p[j]!.x;
      const dy = p[i]!.y - p[j]!.y;
      a[i]![j] = kernel(dx * dx + dy * dy);
    }
    const affine = [1, p[i]!.x, p[i]!.y];
    for (let k = 0; k < 3; k++) {
      a[i]![n + k] = affine[k]!;
      a[n + k]![i] = affine[k]!;
    }
  }
  const pad = [0, 0, 0];
  const wx = solve(a, [...to.map(({ x }) => x / unit), ...pad]);
  const wy = solve(a, [...to.map(({ y }) => y / unit), ...pad]);
  return ({ x, y }) => {
    const u = x / unit;
    const v = y / unit;
    let fx = wx[n]! + wx[n + 1]! * u + wx[n + 2]! * v;
    let fy = wy[n]! + wy[n + 1]! * u + wy[n + 2]! * v;
    for (let i = 0; i < n; i++) {
      const dx = u - p[i]!.x;
      const dy = v - p[i]!.y;
      const k = kernel(dx * dx + dy * dy);
      fx += wx[i]! * k;
      fy += wy[i]! * k;
    }
    return { x: fx * unit, y: fy * unit };
  };
}

// The square root of the map's area scale at a point: how much a dot's radius grows.
export function radiusScale(map: (point: Point) => Point, { x, y }: Point): number {
  const h = 0.5;
  const right = map({ x: x + h, y });
  const left = map({ x: x - h, y });
  const down = map({ x, y: y + h });
  const up = map({ x, y: y - h });
  const ax = (right.x - left.x) / (2 * h);
  const ay = (right.y - left.y) / (2 * h);
  const bx = (down.x - up.x) / (2 * h);
  const by = (down.y - up.y) / (2 * h);
  return Math.sqrt(Math.abs(ax * by - ay * bx));
}
